import { useEffect, useState } from "react";
import drinksApi from "../../api/drinks";
import Swal from "sweetalert2";

interface SwapDrinkDialogProps {
  isOpen: boolean;
  onClose: () => void;
  order: any | null;
  onSwap: (order: any, drinkId: string | null) => Promise<void>;
}

interface Drink {
  _id: string;
  name: string;
  type: string;
  description?: string;
  imageUrl?: string;
  isActive?: boolean;
}

const SwapDrinkDialog = ({
  isOpen,
  onClose,
  order,
  onSwap,
}: SwapDrinkDialogProps) => {
  const [drinks, setDrinks] = useState<Drink[]>([]);
  const [selectedDrinkId, setSelectedDrinkId] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [fetching, setFetching] = useState<boolean>(false);
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const currentDrink =
    order?.drinks && order.drinks.length > 0
      ? order.drinks[order.drinkIndex || 0]?.drinkId || order.drinkId
      : order?.drinkId;
  const currentDrinkId = currentDrink?._id || currentDrink || "";
  const hasExistingDrink = Boolean(currentDrinkId);

  useEffect(() => {
    if (!isOpen) return;
    const fetchDrinks = async () => {
      try {
        setFetching(true);
        const data = await drinksApi.getDrinks({ limit: 100 });
        const fetched = Array.isArray(data) ? data : data?.drinks || [];
        setDrinks(fetched.filter((d: Drink) => d.isActive !== false));
      } catch (e) {
        console.error("Failed to fetch drinks", e);
        Swal.fire({
          title: "Error",
          text: "Failed to load drinks",
          icon: "error",
        });
      } finally {
        setFetching(false);
      }
    };
    fetchDrinks();
  }, [isOpen]);

  useEffect(() => {
    if (currentDrinkId) {
      setSelectedDrinkId(typeof currentDrinkId === "string" ? currentDrinkId : currentDrinkId._id || "");
    } else {
      setSelectedDrinkId("");
    }
  }, [order, currentDrinkId]);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    if (!order) return;
    if (selectedDrinkId === currentDrinkId && hasExistingDrink) {
      Swal.fire({
        title: "No change",
        text: "Please select a different drink or remove it",
        icon: "info",
      });
      return;
    }

    try {
      setLoading(true);
      await onSwap(order, selectedDrinkId || null);
      onClose();
    } catch (e) {
      // onSwap handles errors
    } finally {
      setLoading(false);
    }
  };

  const filteredDrinks = drinks.filter((drink: Drink) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      drink.name?.toLowerCase().includes(q) ||
      drink.type?.toLowerCase().includes(q)
    );
  });

  const selectedDrink = drinks.find((d: Drink) => d._id === selectedDrinkId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 px-3">
      <div className="bg-white w-full max-w-md rounded-lg shadow-lg">
        <div className="px-5 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">
            {hasExistingDrink ? "Swap Drink" : "Add Drink to Order"}
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            {order ? `Order for ${order?.userId?.name || "Customer"}` : ""}
            {order?.drinkIndex !== undefined && order?.peopleCount > 1
              ? ` (Person #${order.drinkIndex + 1})`
              : ""}
          </p>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Drink
            </label>
            <div className="text-sm text-gray-900 font-medium">
              {currentDrink?.name ? (
                <span className="flex items-center gap-2">
                  <span>{currentDrink.name}</span>
                  <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${
                    currentDrink.type === "coffee"
                      ? "bg-amber-100 text-amber-800"
                      : "bg-emerald-100 text-emerald-800"
                  }`}>
                    {currentDrink.type === "coffee" ? "Coffee" : "Blend"}
                  </span>
                </span>
              ) : (
                <span className="text-gray-400 italic">None currently attached</span>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {hasExistingDrink ? "Select New Drink" : "Select Drink to Add"}
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setDropdownOpen((o) => !o)}
                disabled={fetching}
                className="w-full flex justify-between items-center px-3 py-2 text-sm rounded-lg border border-gray-300 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
              >
                <span className="truncate text-left">
                  {selectedDrinkId === "" ? (
                    <span className="text-gray-500 italic">
                      {hasExistingDrink ? "None (Remove Drink from Order)" : "Select a drink..."}
                    </span>
                  ) : selectedDrink ? (
                    <span className="flex items-center gap-2">
                      <span>{selectedDrink.name}</span>
                      <span className={`px-1.5 py-0.2 text-[9px] font-bold rounded ${
                        selectedDrink.type === "coffee"
                          ? "bg-amber-100 text-amber-800"
                          : "bg-emerald-100 text-emerald-800"
                      }`}>
                        {selectedDrink.type === "coffee" ? "Coffee" : "Blend"}
                      </span>
                    </span>
                  ) : (
                    "Select a drink"
                  )}
                </span>
                <svg
                  className={`h-4 w-4 text-gray-500 transition-transform ${
                    dropdownOpen ? "rotate-180" : ""
                  }`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.24 4.5a.75.75 0 01-1.08 0l-4.24-4.5a.75.75 0 01.02-1.06z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              {dropdownOpen && (
                <div className="absolute z-10 mt-2 left-0 right-0 rounded-lg border border-gray-200 bg-white shadow-lg">
                  <div className="p-2 border-b border-gray-100">
                    <input
                      autoFocus
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search drinks..."
                      className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                    />
                  </div>
                  <div className="max-h-56 overflow-auto">
                    {hasExistingDrink && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedDrinkId("");
                          setDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-sm border-b border-gray-100 text-red-600 hover:bg-red-50 flex items-center justify-between ${
                          selectedDrinkId === "" ? "bg-red-50 font-medium" : ""
                        }`}
                      >
                        <span>None (Remove Drink)</span>
                        {selectedDrinkId === "" && <span>Selected</span>}
                      </button>
                    )}
                    {filteredDrinks.map((drink: Drink) => (
                      <button
                        key={drink._id}
                        type="button"
                        onClick={() => {
                          setSelectedDrinkId(drink._id);
                          setDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center justify-between ${
                          selectedDrinkId === drink._id
                            ? "bg-indigo-50 text-indigo-700 font-medium"
                            : "text-gray-800"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span>{drink.name}</span>
                          <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded ${
                            drink.type === "coffee"
                              ? "bg-amber-100 text-amber-800"
                              : "bg-emerald-100 text-emerald-800"
                          }`}>
                            {drink.type === "coffee" ? "Coffee" : "Blend"}
                          </span>
                        </div>
                        {selectedDrinkId === drink._id && (
                          <span className="text-xs text-indigo-600 font-bold">Selected</span>
                        )}
                      </button>
                    ))}
                    {filteredDrinks.length === 0 && (
                      <div className="px-4 py-3 text-sm text-gray-500 text-center">
                        No drinks found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-gray-200 flex justify-end space-x-2">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={loading || fetching}
            className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? "Updating..." : hasExistingDrink ? "Confirm Swap" : "Add Drink"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SwapDrinkDialog;
